import React from 'react';
import { useTheme, styled  } from '@mui/material/styles';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';

import Container from '../../../components/Container';
import Main from '../../../layouts/Main';

import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider } from '@mui/material';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';


const contributionLimits: (string | number)[][] = [
  ['From / To', 'Candidate or Candidate\'s Committee', 'Political Action Committee (PAC)', 'People\'s Committee', 'State Party Committee', 'Local (Ward/Town/City) Party Committee (LPC)', 'Ballot Question Committee (BQC)'],
  ['Individual', 1000, 500, 222, 5000, 5000, 'No Limit'],
  ['Lobbyist', 200, 200, 222, 200, 200, 'No Limit'],
  ['Statewide Candidate\'s Committee', 100, 'No Limit', 0, 'No Limit', 'No Limit', 'No Limit'],
  ['County, Legislative, Municipal or Other Candidate or Candidate\'s Committee', 100, 'No Limit', 0, 'No Limit', 'No Limit', 'No Limit'],
  ['Political Action Committee (PAC)', 500, 500, 0, 5000, 5000, 'No Limit'],
  ['People\'s Committee', 500, 500, 0, 5000, 5000, 'No Limit'],
  ['State Party Committee', 3000, 500, 0, '-', '5000', 'No Limit'],
  ['Local (Ward/Town/City) Party Committee', 1000, 500, 0, 5000, 5000, 'No Limit'],
  ['Ballot Question Committee', 0, 0, 0, 0, 0, 'No Limit']
];

const notes: { id: number, label: string; content: string }[] = [
  { id: 1, label: 'Note A', content: 'The annual cash limit from an individual to a candidate or committee is $50.' },
  { id: 2, label: 'Note B', content: 'Contributions from federal political committees and other unregistered, non-Massachusetts political committees are prohibited. Exception: Independent Expenditure PACs, Ballot Question Committees, legal defense, recount, and inaugural funds may receive unlimited funds from such sources.' },
  { id: 3, label: 'Note C', content: 'Legal defense, recount, and inaugural funds are not subject to contribution limits. See <a href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/M-10-02.pdf" target="_blank">M-10-02</a>.' },
];

const additionalNotes: {id: number, title: string, content: string}[] = [
  {
    id: 1,
    title: 'PACs',
    content: 'PACs must organize with OCPF under M.G.L. c. 55 before they may contribute to Massachusetts candidates or 1 committees. Limits do not apply to Independent Expenditure PACs. (Independent Expenditure PACs may not contribute to candidates or other political committees, except for other Independent Expenditure PACs or ballot question committees.) Please see 970 CMR 2.22 and OCPF’s interpretive bulletin concerning Independent Expenditure PACs, <a href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/IB-10-03.pdf" target="_blank">IB-10-03</a>.'
  },
  {
    id: 2,
    title: 'People\'s Committees',
    content: 'After six months in existence, a PAC that has received contributions from individuals of $222 or less per year and contributed to five or more candidates may request a change in its status to that of a people\'s committee. The maximum contribution from an individual to a people\'s committee is adjusted biennially by OCPF. The $222 figure is in effect for 2026 and 2027.'
  },
  {
    id: 3,
    title: 'Contributions by Individuals',
    content: 'Individuals under 18 years of age have an aggregate contribution limit of $25 per year. There is no limit on how much a candidate may contribute to his or her own campaign, though the maximum amount that certain candidates may loan varies by the office sought (click <a href="https://ocpf.us/PublicSearch/ViewDocument?id=2582" target="_blank">here</a> for the loan limits chart). Contact OCPF for information concerning limits on loans from state candidates to their own campaigns.'
  },
  {
    id: 4,
    title: 'Contributions to Party Committees',
    content: 'The maximum annual aggregate contribution that may be made by an individual, lobbyist, PAC, people\'s committee or party committee to all committees of any one party, including those on the state and local level, is $5,000.'
  },
  {
    id: 5,
    title: 'Statewide Candidates',
    content: 'Include those running for or holding the office of governor, lieutenant governor, attorney general, treasurer/receiver general, auditor and secretary of the commonwealth.'
  },
  {
    id: 6,
    title: 'Candidates Certified to Receive Public Funds',
    content: 'No candidate\'s committee that receives public financing pursuant to M.G.L. c. 55C may make a contribution to another political committee during the calendar year in which the candidate\'s committee receives public financing, except that a committee that receives public financing may pay a political party committee for goods or services provided by the political party committee to the candidate\'s committee.'
  },
  {
    id: 7,
    title: 'Contributions from a Candidate\'s Personal Funds',
    content: 'Contributions from a candidate\'s personal funds to another candidate are subject to the $1,000 individual limit, not the $100 committee limit.'
  },
  {
    id: 8,
    title: 'Contributions from Candidates to PACs',
    content: 'A candidate is prohibited from "financing" a political action committee (Chapter 55, Section 5A). Please see OCPF\'s advisory opinion, <a href="https://ocpf2.blob.core.windows.net/pdf/legaldocs/AO-11-05.pdf" target="_blank">AO-11-05</a>.'
  },
  {
    id: 9,
    title: 'Total PAC Contributions',
    content: 'The aggregate annual amount a state or county candidate may accept from all PACs in a calendar year is limited by M.G.L. c.55, s.6A. For example, a candidate for the Senate may not accept more than $18,750 in total PAC contributions and a candidate for the House may not accept more than $7,500. Candidates for municipal office are not subject to any such annual aggregate restriction.'
  },
  {
    id: 10,
    title: 'Contributions from a PAC, People\'s Committee or Party Committee to a Ballot Question Committee',
    content: 'Contributions from a PAC, people\'s committee or party committee to a ballot question committee are not subject to limitation but must be consistent with the principle for which the contributing committee was organized.'
  },
  {
    id: 11,
    title: 'Party Contributions to Candidates',
    content: 'This limit applies to monetary contributions only. There is no limit on in-kind contributions by a party committee to an individual candidate, but for candidates seeking public financing, in-kind contributions received count toward expenditure limits (970 CMR 4.11).'
  },
  {
    id: 12,
    title: 'Local Party Committees',
    content: 'A local party committee may contribute up to an aggregate of $5,000 in a calendar year to all ward, town, city and state committees of the same political party.'
  },
  {
    id: 13,
    title: 'Contributions Among Ballot Question Committees',
    content: 'A ballot question committee may contribute to another ballot question committee without limitation, provided such contributions are "consistent with the purpose for which [the contributing committee] was organized."'
  }
];

const superScripts: { id: number, x: number, y: number, additionalNoteId: number[]}[] = [
  { id: 1, x: 2, y: 0, additionalNoteId: [1] },
  { id: 2, x: 3, y: 0, additionalNoteId: [2] },

  { id: 3, x: 0, y: 1, additionalNoteId: [3] },
  { id: 4, x: 4, y: 1, additionalNoteId: [4] },
  { id: 5, x: 5, y: 1, additionalNoteId: [4] },

  { id: 6, x: 4, y: 2, additionalNoteId: [4] },
  { id: 7, x: 5, y: 2, additionalNoteId: [4] },

  { id: 8, x: 0, y: 3, additionalNoteId: [5] },
  { id: 9, x: 1, y: 3, additionalNoteId: [6,7] },
  { id: 10, x: 2, y: 3, additionalNoteId: [6,8] },
  { id: 11, x: 4, y: 3, additionalNoteId: [6] },
  { id: 12, x: 5, y: 3, additionalNoteId: [6] },
  { id: 13, x: 6, y: 3, additionalNoteId: [6] },

  { id: 14, x: 1, y: 4, additionalNoteId: [7] },
  { id: 15, x: 2, y: 4, additionalNoteId: [8] },

  { id: 16, x: 0, y: 5, additionalNoteId: [1] },
  { id: 17, x: 1, y: 5, additionalNoteId: [9] },
  { id: 18, x: 4, y: 5, additionalNoteId: [4] },
  { id: 19, x: 5, y: 5, additionalNoteId: [4] },
  { id: 20, x: 6, y: 5, additionalNoteId: [10] },

  { id: 21, x: 4, y: 6, additionalNoteId: [4] },
  { id: 22, x: 5, y: 6, additionalNoteId: [4] },
  { id: 23, x: 6, y: 6, additionalNoteId: [10] },

  { id: 24, x: 1, y: 7, additionalNoteId: [11] },
  { id: 25, x: 5, y: 7, additionalNoteId: [4] },
  { id: 26, x: 6, y: 7, additionalNoteId: [10] },

  { id: 27, x: 1, y: 8, additionalNoteId: [11] },
  { id: 28, x: 4, y: 8, additionalNoteId: [4, 12] },
  { id: 29, x: 5, y: 8, additionalNoteId: [4, 12] },
  { id: 30, x: 6, y: 8, additionalNoteId: [10] },

  { id: 31, x: 6, y: 9, additionalNoteId: [13] },
]

const ContributionLimits = (): JSX.Element => {
  
  document.title = 'OCPF - Contribution Limits';
  
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const LIGHT_GREY = 'rgba(0, 0, 0, 0.04)';

  const formatCellValue = (value: string | number, row: number, cell: number) => {
    let v = value.toString();
    if (typeof value === 'number') {
      v = value === 0 ? '$0' : `$${value.toLocaleString()}`;
    }

    //find the superscripts for each cell
    let noteNum = superScripts.find(s => s.x === cell && s.y === row)?.additionalNoteId
    let supScripts: JSX.Element[] = [];
    if(noteNum && noteNum.length) {
      let notSubscripts = noteNum.length > 1 ? noteNum.join(', '): noteNum[0].toString();

      supScripts.push(<sup key={notSubscripts}><a href="#footnotes-table" style={{ textDecoration: 'none', color: 'inherit' }}>{notSubscripts}</a></sup>);
    }
      return (
        <>  
          {v}
          {supScripts}
        </>
      )
  };

  const getCellBackgroundColor = (value: string | number, rowIndex: number, cellIndex: number) => {
    if (rowIndex === 0 || cellIndex === 0) return 'transparent';
    
    // Only apply alternating color for non-header rows
    return rowIndex % 2 === 1 ? LIGHT_GREY : 'transparent';
  };

  return (
    <Main>
      <Box
        bgcolor={theme.palette.background.default}
        position={'relative'}
        minHeight={'calc(100vh - 247px)'}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        height={1}
        marginTop={-12}
        paddingTop={12}>

        <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
          <Box>
              <Typography
                component="h1"
                variant="h4"
                align={'left'}
                data-aos={'fade-up'}
                gutterBottom
                sx={{
                  fontWeight: 700,
                }}
                paddingTop={3}>
                Annual Campaign Contribution Limits <Typography component="span" variant="caption" color="text.secondary">per calendar year</Typography>
              </Typography>

              <Divider/>

              <Box sx={{ marginTop: 4 }}>
                <TableContainer component={Paper}>
                  <Table sx={{ minWidth: 650 }} aria-label="Contribution Limits Table">
                    <TableHead>
                      <TableRow key="1">
                        {contributionLimits[0].map((cell, index) => (
                          <TableCell
                            key={index}
                            align="center"
                            component="th"
                            scope="col"
                            sx={{
                              fontWeight: 'bold',
                              backgroundColor: getCellBackgroundColor(cell, 0, index)
                            }}
                          >
                            {formatCellValue(cell, 0, index)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contributionLimits.slice(1).map((row, rowIndex) => (
                        <TableRow key={rowIndex+1}>
                          {row.map((cell, cellIndex) => (
                            <TableCell
                              key={cellIndex}
                              align="center"
                              component={cellIndex === 0 ? 'th' : 'td'}
                              scope={cellIndex === 0 ? 'row' : undefined}
                              sx={{
                                fontWeight: cellIndex === 0 ? 'bold' : 'normal',
                                backgroundColor: getCellBackgroundColor(cell, rowIndex + 1, cellIndex)
                              }}
                            >
                              {formatCellValue(cell, rowIndex + 1, cellIndex)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
          </Box>
          
            <Box sx={{ marginTop: 4 }}>
                {notes.map((note) => (
                  <Box key={note.id} sx={{ marginBottom: 2 }}>
                    <Typography variant="body1" fontWeight="bold">{note.label}</Typography>
                    <Typography variant="body2"><span dangerouslySetInnerHTML={{__html: note.content}}></span></Typography>
                  </Box>
                ))}
            </Box>

            <Box sx={{ marginTop: 4 }}>
              <TableContainer component={Paper}>
                <Table id="footnotes-table" aria-label="Additional superscript notes for contribution limits">
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        component="th" 
                        scope="col" 
                        colSpan={3} 
                        align="center" 
                        sx={{backgroundColor: LIGHT_GREY}}
                      >
                        <strong>Additional Notes</strong>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="col">Note #</TableCell>
                      <TableCell component="th" scope="col">Title</TableCell>
                      <TableCell component="th" scope="col">Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {additionalNotes.map((note) => (
                      <TableRow key={note.id}>
                        <TableCell component="th" scope="row">{note.id}</TableCell>
                        <TableCell>{note.title}</TableCell>
                        <TableCell><span dangerouslySetInnerHTML={{__html: note.content}}></span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Container>
      </Box>
    </Main>
  );
};

export default ContributionLimits;
